<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import DBG from "../code/DBG";
    import type ProjSettings from "../code/model/ProjSettings";
    import Task from "../code/Task";
    import Chart from "chart.js/auto";

    import "chartjs-adapter-moment";
    import "chartjs-plugin-dragdata";
    import moment from "moment";
    import ProjectData from "../code/ProjectData";
    import Intercoms from "../code/Intercoms";
    import GlobalSettings from "../code/GlobalSettings";
    import Legend from "./Legend.svelte";

    export let settings: ProjSettings;
    export let chartData: Task[];
    export let wideChart: boolean = false;
    export let showChartLegend: boolean = true;

    let gDatasetToTask;
    let gTaskGuidToIdx;
    let gColors = [
        "#009cff",
        "#ec008c",
        "#031747",
        "rgba(255, 99, 132, 1)",
        "rgba(54, 162, 235, 1)",
        "rgba(255, 206, 86, 1)",
        "rgba(75, 192, 192, 1)",
        "rgba(153, 102, 255, 1)",
        "rgba(255, 159, 64, 1)",
        "#06c2fc",
        "#8F754F",
        "#fc4cc7",
        "#385F71",
        "#2B4162",
        "#D7B377"
    ];
    let taskTypeToColor = {};
    let colorTypeArray = undefined;

    let myChart;
    let chartMounted: boolean = false;
    onMount(() => {
        DBG.log("Chart - onMount", settings, chartData);
        setupChart();
        chartMounted = true;
    });

    function setupChart() {
        // Step #1 - parse chart data to get labels (i.e., dates)
        let datasets = convertProjectDataToDatasets();
        let gChart: Chart;
        DBG.log("Chart - setupChart - datasets:", datasets);

        let elem: HTMLCanvasElement = document.getElementById(
            "myChart"
        ) as HTMLCanvasElement;
        const ctx = elem.getContext("2d");
        myChart = gChart = new Chart(ctx, {
            type: "line",
            data: {
                //labels: ['Red', 'Blue', 'Yellow', 'Green', 'Purple', 'Orange'],
                datasets: datasets,
            },

            options: {
                aspectRatio: wideChart ? 3 : 1.5,
                onClick: (evt, elements) => {
                    let e: Event = evt as unknown as Event;

                    const points = gChart.getElementsAtEventForMode(
                        e,
                        "nearest",
                        { intersect: true },
                        true
                    );

                    DBG.log("Chart - OnClick", evt, elements, points);
                    //selectTask("");
                },
                plugins: {
                    //@ts-ignore
                    dragData: {
                        round: 0,
                        dragX: true,
                        dragY: false,
                        showTooltip: true,
                        onDragStart: function (
                            e,
                            datasetIndex,
                            index,
                            value
                        ) {},
                        onDrag: function (e, datasetIndex, index, value) {
                            e.target.style.cursor = "grabbing";
                            //console.log("Drag Value: ", value.x)
                        },
                        onDragEnd: function (e, datasetIndex, index, value) {
                            e.target.style.cursor = "default";
                            DBG.log("Drag end:", datasetIndex, index, value);
                            updateTask(datasetIndex, index, value);
                        },
                    },
                    legend: {
                        position: "bottom",
                        display: showChartLegend,
                    },
                },

                scales: {
                    x: {
                        type: "time",
                        ticks: {
                            callback(v, t, e) {                              
                                return moment(v).format("YYYY-MM-DD");
                            },
                        },
                        time: {
                            displayFormats: {
                                day: "YYYY-MM-DD",
                            },
                            unit: "day",
                        },
                    },

                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: "Sector",
                        },
                        ticks: {
                            // Include a dollar sign in the ticks
                            callback: function (value, index, ticks) {
                                return parseInt(`${value}`).toString();
                            },
                        },
                    },
                },
            },
        });
    }

    function convertProjectDataToDatasets() {
        let datasets = [];
        gDatasetToTask = [];
        let crtColor = 0;
        gTaskGuidToIdx = {};
        if (!colorTypeArray) {
            colorTypeArray = [];
        }
        DBG.log("Chart - convertProjectDataToDatasets");
        chartData.forEach((element, i) => {
            let taskColorIdx: number;
            let ttype = element.taskType ?? "undefined";

            if (taskTypeToColor[ttype] != undefined) {
                taskColorIdx = taskTypeToColor[ttype];
            } else {
                taskColorIdx = crtColor % gColors.length;
                taskTypeToColor[ttype] = crtColor++;
                colorTypeArray.push({
                    color: gColors[taskColorIdx],
                    text: ttype,
                });
            }

            DBG.log(
                `Chart - task type: ${element.taskType} -> color: ${taskTypeToColor[ttype]}`
            );
            datasets.push({
                data: [
                    { x: element.startDate, y: element.startSector },
                    {
                        x: element.endDate,
                        y: GlobalSettings.usingSectorEnd()
                            ? element.endSector
                            : element.startSector + 1,
                    },
                ],
                borderColor: gColors[taskTypeToColor[ttype] % gColors.length],
                backgroundColor:
                    gColors[taskTypeToColor[ttype] % gColors.length],
                borderWidth: 1,
                label: element.name,
            });
            gDatasetToTask[i] = element.guid;
            gTaskGuidToIdx[element.guid] = i;
        });
        colorTypeArray = colorTypeArray;

        DBG.log(
            "Chart - convertProjectDataToDatasets - colorTypeArray",
            colorTypeArray
        );
        return datasets;
    }

    function updateTask(datasetIndex, index, value) {
        let taskGuid = gDatasetToTask[datasetIndex];
        let tsk = ProjectData.taskGuidToTask[taskGuid];
        let date = new Date(value.x);

        if (index == 0) {
            DBG.log(
                "Chart - updateTask - Setting start date for",
                taskGuid,
                value,
                date
            );
            Office.context.document.setTaskFieldAsync(
                taskGuid,
                Office.ProjectTaskFields.Start,
                moment(date).format("ddd DD/MM/YYYY"),
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        DBG.log("TaskPan - updateTask - SUCCESS", result);
                        delayedGetTaskProperties(taskGuid);
                    } else {
                        DBG.log("TaskPan - updateTask - FAILED", result);
                    }
                }
            );
        } else {
            DBG.log(
                "Chart - updateTask - Setting end date for",
                taskGuid,
                value,
                date
            );
            Office.context.document.setTaskFieldAsync(
                taskGuid,
                Office.ProjectTaskFields.Finish,
                moment(date).format("ddd DD/MM/YYYY"),
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        DBG.log("TaskPan - updateTask - SUCCESS", result);
                        delayedGetTaskProperties(taskGuid);
                    } else {
                        DBG.log("TaskPan - updateTask - FAILED", result);
                    }
                }
            );
        }
    }

    /**
     * After a task is modified, other properties could change (e.g. if duration is fixed). Re-read it and update chart.
     * @param taskGuid
     */
    function delayedGetTaskProperties(taskGuid) {
        setTimeout(() => {
            DBG.log("TaskPan - delayedGetTaskProperties", taskGuid);
            Task.ReadData(taskGuid).then((tsk) => {
                let idx = gTaskGuidToIdx[taskGuid];
                chartData[idx] = tsk;
                DBG.log("TaskPan - reset chartData", idx, tsk);

                updateChartFromDatasets();
            });
        }, 50);
    }
    function updateChartFromDatasets() {
        let datasets = convertProjectDataToDatasets();

        // Get original animation options.
        let anim = myChart.options.animation;
        myChart.options.animation = { duration: 0 };
        myChart.config.data = {
            datasets: datasets,
        };
        myChart.update();
        myChart.options.animation = anim;
    }

    let eventsSubscribed: any[] = new Array();
    Intercoms.listen(
        "chart.refresh",
        (newChartData) => {
            DBG.log("Chart - chart.refresh", chartMounted, newChartData);
            if (chartMounted) {
                // chartData = newChartData;
                updateChartFromDatasets();
            }
        },
        eventsSubscribed
    );

    onDestroy(() => {
        DBG.log("Chart - onDestroy");
        Intercoms.unsubscribeAll(eventsSubscribed);
        chartMounted = false;
    });
    /* $:{
        if(chartData){
            updateChartFromDatasets();
        }
    }*/
</script>

<div id="chart-holder" class:wideChart>
    <canvas id="myChart" />
</div>
{#if colorTypeArray}
    <Legend taskTypeToColor={colorTypeArray} />
{/if}

<style>
    #myChart {
        width: 100%;
    }
    #chart-holder {
        width: 100%;
    }
    #chart-holder.wideChart {
        width: 2000px;
    }
</style>
